import React, { useEffect, useState } from 'react';
import {
  Formik,
  useField,
  Form,
  FormikHelpers,
  useFormikContext,
} from 'formik';
import { useTranslation } from 'react-i18next';
import { Button, Divider, Dropdown, Message } from 'semantic-ui-react';
import countries from 'utils/countries';
import request from 'utils/api/request';
import { useParams } from 'react-router-dom';
import { Flex } from 'common/layout/helpers';
import { ApiError } from 'utils/api';
import {
  CheckboxAccordionField,
  CheckboxAccordionOption,
} from 'components/CheckboxAccordion/CheckboxAccordionField';
import { uniq } from 'lodash-es';

/** Based on https://github.com/e-flux-platform/contracts/blob/master/protobuf/payments/v1/payment_type.proto */
enum PaymentType {
  UNSPECIFIED = 'UNSPECIFIED',
  CARD = 'card',
  DIRECT_DEBIT = 'direct-debit',
  BANK_TRANSFER = 'bank-transfer',
  DIGITAL_WALLET = 'digital-wallet',
}

/** Based on https://github.com/e-flux-platform/contracts/blob/master/protobuf/payments/v1/payment_method.proto */
enum PaymentMethod {
  UNSPECIFIED = 'UNSPECIFIED',
  // Cards
  AMERICAN_EXPRESS = 'american-express',
  BANCONTACT = 'bancontact',
  CARTES_BANCAIRES = 'cartes-bancaires',
  DINERS_CLUB = 'diners-club',
  MASTERCARD = 'mastercard',
  VISA = 'visa',

  // Digital Wallets
  PAYPAL = 'paypal',
  APPLE_PAY = 'apple-pay',
  GOOGLE_PAY = 'google-pay',

  // Bank Transfers
  EPS = 'eps',
  GIROPAY = 'giropay',
  SOFORT = 'sofort',
  IDEAL = 'ideal',
  PRZELEWY24 = 'przelewy24',

  // Direct Debit
  SEPA_DIRECT_DEBIT = 'sepa-direct-debit',
}

/* Based on https://github.com/e-flux-platform/contracts/blob/master/protobuf/payments/v1/payment_provider.proto */
enum PaymentProvider {
  // This is an unknown payment provider.
  // This value is not used in practice.
  UNSPECIFIED = 'UNSPECIFIED',

  // This is the Checkout.com payment provider.
  CHECKOUT = 'checkout',
}

type PaymentMethodElement = {
  countryCode?: string;
  enabled: boolean;
  paymentMethod: PaymentMethod;
  paymentProvider: PaymentProvider;
  paymentType: PaymentType;
};

function buildPaymentMethodOptions(
  t: (key: string, defaultValue: string) => string,
  elements: PaymentMethodElement[],
  countryFilter?: string
) {
  const options: CheckboxAccordionOption[] = [];
  for (let i = 0; i < elements.length; i++) {
    if (elements[i].countryCode !== countryFilter) {
      continue;
    }

    const item = elements[i];

    switch (item.paymentMethod) {
      case PaymentMethod.VISA:
        options.push({
          label: t(
            'providerPaymentMethods.visaAndVisaDebit',
            'Visa and Visa Debit'
          ),
          checked: item.enabled,
          name: `methods[${i}].enabled`,
        });
        break;
      case PaymentMethod.MASTERCARD:
        options.push({
          label: t(
            'providerPaymentMethods.mastercardAndMaestro',
            'Mastercard and Mastercards Debit (Maestro)'
          ),
          checked: item.enabled,
          name: `methods[${i}].enabled`,
        });
        break;
      case PaymentMethod.AMERICAN_EXPRESS:
        options.push({
          label: t('providerPaymentMethods.amex', 'American Express'),
          checked: item.enabled,
          name: `methods[${i}].enabled`,
        });
        break;
    }
  }

  return options;
}

function buildBankTransferOptions(
  t: (key: string, defaultValue: string) => string,
  elements: PaymentMethodElement[],
  countryFilter?: string
) {
  const options: CheckboxAccordionOption[] = [];
  for (let i = 0; i < elements.length; i++) {
    if (elements[i].countryCode !== countryFilter) {
      continue;
    }

    const item = elements[i];

    switch (item.paymentMethod) {
      case PaymentMethod.IDEAL:
        options.push({
          label: t('providerPaymentMethods.ideal', 'iDeal'),
          checked: item.enabled,
          name: `methods[${i}].enabled`,
        });
        break;
      case PaymentMethod.SEPA_DIRECT_DEBIT:
        options.push({
          label: t(
            'providerPaymentMethods.sepaDirectDebit',
            'SEPA Direct Debit'
          ),
          checked: item.enabled,
          name: `methods[${i}].enabled`,
        });
        break;
    }
  }

  return options;
}

function buildDigitalWalletOptions(
  t: (key: string, defaultValue: string) => string,
  elements: PaymentMethodElement[],
  countryFilter?: string
) {
  const options: CheckboxAccordionOption[] = [];
  for (let i = 0; i < elements.length; i++) {
    if (elements[i].countryCode !== countryFilter) {
      continue;
    }

    const item = elements[i];

    switch (item.paymentMethod) {
      case PaymentMethod.APPLE_PAY:
        options.push({
          label: t('providerPaymentMethods.applePay', 'Apple Pay'),
          checked: item.enabled,
          name: `methods[${i}].enabled`,
        });
        break;
      case PaymentMethod.GOOGLE_PAY:
        options.push({
          label: t('providerPaymentMethods.googlePay', 'Google Pay'),
          checked: item.enabled,
          name: `methods[${i}].enabled`,
        });
        break;
      case PaymentMethod.PAYPAL:
        options.push({
          label: t('providerPaymentMethods.paypal', 'PayPal'),
          checked: item.enabled,
          name: `methods[${i}].enabled`,
        });
        break;
    }
  }

  return options;
}

export const ProviderPaymentMethods = () => {
  const { t } = useTranslation();
  const { id: providerId } = useParams<{ id: string }>();
  const [providerPaymentMethods, setProviderPaymentMethods] =
    useState<PaymentMethodElement[]>();

  useEffect(() => {
    request<{ data: PaymentMethodElement[] }>({
      method: 'GET',
      path: `/1/providers/${providerId}/payment-methods`,
    })
      .then((response) => {
        setProviderPaymentMethods(response.data);
      })
      .catch((e) => {
        console.log(e);
      });
  }, [providerId]);

  const submitHandler = (
    data: { methods: PaymentMethodElement[] },
    helpers: FormikHelpers<{ methods: PaymentMethodElement[] }>
  ) => {
    request({
      path: `/1/providers/${providerId}/payment-methods`,
      method: 'POST',
      body: { paymentMethods: data.methods },
    })
      .then(() => {
        helpers.setStatus({ success: true });
        helpers.setSubmitting(false);
      })
      .catch((e) => {
        helpers.setErrors({ methods: `error ${(e as ApiError).message}` });
        helpers.setStatus({ success: false });
        helpers.setSubmitting(false);
        console.error(e);
      });
  };

  return (
    <div>
      {providerPaymentMethods && (
        <Formik
          initialValues={{ methods: providerPaymentMethods || [] }}
          onSubmit={submitHandler}>
          <ProviderPaymentMethodsForm />
        </Formik>
      )}
    </div>
  );
};

const calculatePossibleCountryOverrides = (
  paymentMethods: PaymentMethodElement[],
  usedCountriesOverrides: string[]
) => {
  return Array.from(
    new Set(
      // This grabs all the countries from the payment methods, not just enabled ones.
      paymentMethods
        .filter(
          (e) =>
            !!e.countryCode && !usedCountriesOverrides.includes(e.countryCode)
        )
        .map((e) => e.countryCode!)
    )
  ).map((country) => {
    return {
      key: country,
      text: countries.find((e) => e.countryCode === country.toUpperCase())
        ?.nameEn,
      value: country,
    };
  });
};

const ProviderPaymentMethodsForm = () => {
  const { t } = useTranslation();

  const [field, meta, helpers] = useField<PaymentMethodElement[]>('methods');
  const { status, isSubmitting } = useFormikContext();

  const [country, setCountry] = useState<string | undefined>();

  const [countryOverrides, setCountryOverrides] = useState<string[]>(
    // This removes duplicates from country codes
    uniq(
      field.value
        .filter((e) => !!e.countryCode && e.enabled)
        .map((e) => e.countryCode!)
    )
  );

  const ccPaymentMethods = buildPaymentMethodOptions(t, field.value);
  const bankTransferPaymentMethods = buildBankTransferOptions(t, field.value);
  const digitalWalletPaymentMethods = buildDigitalWalletOptions(t, field.value);
  const hasDefaultPaymentMethods =
    ccPaymentMethods.length > 0 ||
    bankTransferPaymentMethods.length > 0 ||
    digitalWalletPaymentMethods.length > 0;

  return (
    <Form>
      {hasDefaultPaymentMethods && (
        <>
          <h4>
            {t(
              'providerPaymentMethods.defaultPaymentMethods',
              'Default payment methods'
            )}
          </h4>

          {ccPaymentMethods.length > 0 && (
            <CheckboxAccordionField
              options={ccPaymentMethods}
              title={t(
                'providerPaymentMethods.debutAndCreditCards',
                'Debit and Credit Cards'
              )}
            />
          )}

          {bankTransferPaymentMethods.length > 0 && (
            <CheckboxAccordionField
              options={bankTransferPaymentMethods}
              title={t('providerPaymentMethods.bankTransfer', 'Bank Transfer')}
            />
          )}

          {digitalWalletPaymentMethods.length > 0 && (
            <CheckboxAccordionField
              options={digitalWalletPaymentMethods}
              title={t(
                'providerPaymentMethods.digitalWallets',
                'Digital Wallets'
              )}
            />
          )}

          <Divider />
        </>
      )}

      <h4>
        {t('providerPaymentMethods.countryOverrides', 'Country overrides')}
      </h4>
      <div
        style={{
          display: 'flex',
          width: '100%',
          alignItems: 'center',
          gap: '1rem',
        }}>
        <div style={{ flex: 1, minWidth: '0' }}>
          <Dropdown
            placeholder={
              calculatePossibleCountryOverrides(field.value, countryOverrides)
                .length > 0
                ? t('providerPaymentMethods.country', 'Country')
                : t(
                    'providerPaymentMethods.noCountriesAvailable',
                    'No countries available'
                  )
            }
            fluid
            selection
            options={calculatePossibleCountryOverrides(
              field.value,
              countryOverrides
            )}
            onChange={(_, { value }) => {
              setCountry(value as string);
            }}
            disabled={
              calculatePossibleCountryOverrides(field.value, countryOverrides)
                .length === 0
            }
          />
        </div>

        <div style={{ marginLeft: 'auto' }}>
          <Button
            type="button"
            onClick={() => {
              if (!country || countryOverrides.includes(country)) {
                return;
              }
              setCountryOverrides([...countryOverrides, country]);
            }}>
            {t(
              'providerPaymentMethods.addCountryOverride',
              'Add country override'
            )}
          </Button>
        </div>
      </div>

      <Divider />

      {countryOverrides.map((country) => {
        const countryCCPaymentMethods = buildPaymentMethodOptions(
          t,
          field.value,
          country
        );
        const countryBankTransferPaymentMethods = buildBankTransferOptions(
          t,
          field.value,
          country
        );
        const countryDigitalWalletPaymentMethods = buildDigitalWalletOptions(
          t,
          field.value,
          country
        );

        return (
          <div key={country}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-end',
              }}>
              <h4>
                {
                  countries.find((e) => e.countryCode === country.toUpperCase())
                    ?.nameEn
                }
              </h4>
              <div>
                <Button
                  basic
                  icon="trash"
                  onClick={() => {
                    for (const i in field.value) {
                      if (field.value[i].countryCode === country) {
                        field.value[i].enabled = false;
                      }
                    }

                    setCountryOverrides(
                      countryOverrides.filter((e) => e !== country)
                    );
                  }}
                />
              </div>
            </div>
            {countryCCPaymentMethods.length > 0 && (
              <CheckboxAccordionField
                options={countryCCPaymentMethods}
                title={t(
                  'providerPaymentMethods.debutAndCreditCards',
                  'Debit and Credit Cards'
                )}
              />
            )}
            {countryBankTransferPaymentMethods.length > 0 && (
              <CheckboxAccordionField
                options={countryBankTransferPaymentMethods}
                title={t(
                  'providerPaymentMethods.bankTransfer',
                  'Bank Transfer'
                )}
              />
            )}
            {countryDigitalWalletPaymentMethods.length > 0 && (
              <CheckboxAccordionField
                options={countryDigitalWalletPaymentMethods}
                title={t(
                  'providerPaymentMethods.digitalWallets',
                  'Digital Wallets'
                )}
              />
            )}

            <Divider />
          </div>
        );
      })}

      <Flex justify="flex-end">
        <Button type="submit" disabled={isSubmitting} loading={isSubmitting}>
          {t('common.apply', 'Apply')}
        </Button>
      </Flex>
      {status?.success && (
        <Message success>
          <p>
            {t(
              'editProvider.paymentMethodsSaved',
              'Payment methods saved successfully'
            )}
          </p>
        </Message>
      )}
      {meta.error && <Message error>{meta.error}</Message>}
    </Form>
  );
};
